import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, message } from 'antd';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { OrderType } from '../../../graphql/types/OrderType';
import { CLOSE_ORDER, CloseOrderInput } from '../graphql/mutations/closeOrder';
import { VALIDATE_CLOSE_OPERATION, ValidateCloseOperationResponse } from '../graphql/queries/validateCloseOperation';
import { t } from '../../../i18n';
import { useTranslation } from 'react-i18next';
import ImageUploader from '../../../components/ImageUploader';
import { SiteType } from '../../../graphql/types/SiteType';
import { hasHeadquartersAccess, isFinancialTeam, isKAQC, isCSFollowUp } from '../../../utils/auth';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { useGetAllAccessHubAndWarehousesForRole } from '../../../hooks/useWarehouseQuery';
import {
    ExceptionDictionaryResponse,
    GET_ALL_EXCEPTION
} from '../../delivery/parcels/graphql/queries/getAllException';
import { useStatusInternationalization } from '../../delivery/parcels/hooks/useStatusInternationalization';

// 显示模式枚举
enum DisplayMode {
  SHOW_HUB_WAREHOUSE = 'show-hub-warehouse',
  HIDE_HUB_WAREHOUSE = 'hide-hub-warehouse'
}

interface CloseOrderModalProps {
    visible: boolean;
    onClose: () => void;
    order: OrderType | null;
    onSuccess: () => void;
}

const CloseOrderModal: React.FC<CloseOrderModalProps> = ({
    visible,
    onClose,
    order,
    onSuccess
}) => {
    const [form] = Form.useForm<CloseOrderInput>();
    const apolloClient = useApolloClient();
    const [closeOrder, { loading }] = useMutation(CLOSE_ORDER);
    const [closeType, setCloseType] = useState<string>('');
    const [warehouseListForHubData, setWarehouseListForHubData] = useState<any[]>([]);

    // 查询异常原因字典数据
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const { getExceptionReasonText } = useStatusInternationalization();
    const { i18n } = useTranslation();

    // 获取当前仓库信息
    const { currentWarehouse } = useWarehouseContext();

    // 判断显示模式
    const isHeadquartersStaffRole = hasHeadquartersAccess();
    const isKAQCRole = isKAQC();
    const isFinancialTeamRole = isFinancialTeam();
    const isCSFollowUpRole = isCSFollowUp();

    const getDisplayMode = (): DisplayMode => {
        if ((isHeadquartersStaffRole || isKAQCRole || isFinancialTeamRole || isCSFollowUpRole) && !currentWarehouse) {
            return DisplayMode.SHOW_HUB_WAREHOUSE;
        }
        return DisplayMode.HIDE_HUB_WAREHOUSE;
    };

    const displayMode = getDisplayMode();

    // 数据查询 - 统一使用 useGetAllAccessHubAndWarehousesForRole
    const {
        warehouses: warehouseData,
        loading: warehousesLoading,
        error: warehousesError
    } = useGetAllAccessHubAndWarehousesForRole({
        skip: displayMode === DisplayMode.HIDE_HUB_WAREHOUSE
    });

    const warehouseListData = warehouseData.filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE);

    useEffect(() => {
        if (visible) {
            form.resetFields();
            setCloseType('');
            setWarehouseListForHubData([]);
        }
    }, [visible, form]);

    const handleHubChange = (value: string) => {
        // 获取该hubId对应的warehouse
        const filteredWarehouses = warehouseListData?.filter(warehouse => warehouse.parentSiteId === value) || [];
        setWarehouseListForHubData(filteredWarehouses);
        // 清空仓库选择，因为 hub 改变了
        form.setFieldsValue({
            exceptionWarehouseId: undefined
        });
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            // 如果没有订单数据，直接返回
            if (!order) {
                return;
            }
            
            const input: CloseOrderInput = {
                orderId: order.id?.toString() || '',
                closeType: values.closeType,
                exceptionReasonCode: values.exceptionReasonCode,
                exceptionPriority: values.exceptionPriority || 100,
                ...(closeType !== 'LOST' && values.eventLocation ? { eventLocation: values.eventLocation } : {}),
                ...(closeType !== 'LOST' && values.imageS3Keys ? { imageS3Keys: values.imageS3Keys } : {}),
                // 所有类型都支持Hub和Warehouse
                ...(values.exceptionHubId ? { exceptionHubId: values.exceptionHubId } : {}),
                ...(values.exceptionWarehouseId ? { exceptionWarehouseId: values.exceptionWarehouseId } : {}),
            };

            // 提交前做一次后端规则校验，避免不满足状态仍然发起关单
            const validation = await apolloClient.query<ValidateCloseOperationResponse>({
                query: VALIDATE_CLOSE_OPERATION,
                variables: {
                    orderId: input.orderId,
                    closeType: input.closeType,
                },
                fetchPolicy: 'no-cache',
            });
            if (!validation.data.validateCloseOperation.isValid) {
                const errorMessage = validation.data.validateCloseOperation.errorMessage;
                message.warning(
                    getLocalizedErrorMessage(errorMessage)
                );
                return;
            }

            await closeOrder({
                variables: { input }
            });

            message.success(t('orderClose.closeSuccess'));
            onSuccess();
            onClose();
        } catch (error: any) {
            message.error(error.message || t('orderClose.closeFailed'));
        }
    };

    const handleCloseTypeChange = (value: string) => {
        setCloseType(value);
        form.setFieldsValue({
            exceptionReasonCode: undefined,
            imageS3Keys: undefined,
            eventLocation: undefined,
            // 切换类型时不清空Hub和Warehouse，保持用户选择
        });
        // 清除中心字段的验证错误，让新的验证规则生效
        if (value !== 'LOST' && value !== 'RETURN_SCAN' && value !== 'RETURNED') {
            form.setFields([
                { name: 'exceptionHubId', errors: [] }
            ]);
        }
    };

    /**
     * 获取关单操作中异常原因的显示文本
     * 与 MarkAsException 保持一致，统一使用后端的 secondaryTypeEn
     */
    const getCloseOrderExceptionReasonText = (exception: any, exceptionReasonCode: string): string => {
        // 统一使用通用的显示方式，与 MarkAsException 保持一致
        return getExceptionReasonText(exception, exceptionReasonCode);
    };

    /**
     * 将后端返回的错误消息转换为多语言消息
     */
    const getLocalizedErrorMessage = (errorMessage: string | null | undefined): string => {
        if (!errorMessage) {
            return t('orderClose.closeFailed');
        }

        // 错误消息到翻译键的映射
        const errorMessageMap: Record<string, string> = {
            'Delivery failed can only be applied to orders in DELIVERY_IN_PROGRESS status': 'orderClose.validationErrors.deliveryFailedInvalidStatus',
            'Return scan can only be applied to orders with RETURN next action': 'orderClose.validationErrors.returnScanInvalidNextAction',
            'Return scan requires return warehouse scan completed': 'orderClose.validationErrors.returnScanRequiresWarehouseScan',
            'Returned can only be applied to orders with RETURN_SCAN event record': 'orderClose.validationErrors.returnedRequiresReturnScanEvent',
            'Exception reason code is required for LOST close type': 'orderClose.validationErrors.lostRequiresExceptionReason',
            'Order has already been closed. Cannot close the same order twice.': 'orderClose.validationErrors.alreadyClosed',
        };

        // 处理包含动态参数的错误消息（如订单ID）
        if (errorMessage.startsWith('Order not found with id: ')) {
            const orderId = errorMessage.replace('Order not found with id: ', '');
            return t('orderClose.validationErrors.orderNotFound', { orderId });
        }

        // 查找对应的翻译键
        const translationKey = errorMessageMap[errorMessage];
        if (translationKey) {
            const translated = t(translationKey);
            // 如果翻译键不存在，返回原始消息
            return translated !== translationKey ? translated : errorMessage;
        }

        // 如果没有找到对应的翻译键，返回原始消息
        return errorMessage;
    };

    const getExceptionReasonOptions = () => {
        // 根据关单类型过滤异常原因
        const allowedCodes = closeType === 'LOST' 
            ? ['ERHUB003', 'ERHUB008', 'ERHUB005']
            : closeType === 'DELIVERY_FAILED'
            ? ['ERDL002']
            : [];
        
        // 从异常原因字典中查找对应的异常原因，并使用专门的显示函数格式化显示
        return exceptionList
            .filter(ex => allowedCodes.includes(ex.exceptionReasonCode))
            .map(ex => ({
                value: ex.exceptionReasonCode,
                label: getCloseOrderExceptionReasonText(ex, ex.exceptionReasonCode)
            }));
    };

    return (
        <Modal
            title={t('orderClose.operateClose')}
            open={visible}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText={t('components.button.ok')}
            cancelText={t('components.button.cancel')}
            width={600}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="closeType"
                    label={t('orderClose.fields.closeType')}
                    rules={[{ required: true, message: t('common.required') }]}
                >
                    <Select
                        placeholder={t('common.pleaseSelect')}
                        onChange={handleCloseTypeChange}
                    >
                        <Select.Option value="LOST">{t('orderClose.closeType.LOST')}</Select.Option>
                        <Select.Option value="DELIVERY_FAILED">{t('orderClose.closeType.DELIVERY_FAILED')}</Select.Option>
                        <Select.Option value="RETURN_SCAN">{t('orderClose.closeType.RETURN_SCAN')}</Select.Option>
                        <Select.Option value="RETURNED">{t('orderClose.closeType.RETURNED')}</Select.Option>
                    </Select>
                </Form.Item>

                {(closeType === 'LOST' || closeType === 'DELIVERY_FAILED') && (
                    <Form.Item
                        name="exceptionReasonCode"
                        label={t('orderClose.fields.exceptionReason')}
                        rules={[{ required: true, message: t('common.required') }]}
                    >
                        <Select placeholder={t('common.pleaseSelect')}>
                            {getExceptionReasonOptions().map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {/* 所有类型都支持选择Hub和Warehouse */}
                {displayMode === DisplayMode.SHOW_HUB_WAREHOUSE && (
                    <>
                        <Form.Item
                            name="exceptionHubId"
                            label={t('components.modal.hub')}
                            rules={(closeType === 'LOST' || closeType === 'RETURN_SCAN' || closeType === 'RETURNED') ? [{ required: true, message: t('common.required') }] : []}
                        >
                            <Select
                                placeholder={t('components.modal.selectHub')}
                                allowClear
                                onChange={handleHubChange}
                            >
                                {warehouseData.map(warehouse => warehouse.siteType === SiteType.HUB ? (
                                    <Select.Option
                                        key={warehouse.id}
                                        value={warehouse.id}
                                    >
                                        {warehouse.name}
                                    </Select.Option>
                                ) : null)}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="exceptionWarehouseId"
                            label={t('components.modal.warehouse')}
                        >
                            <Select
                                placeholder={t('components.modal.selectWarehouse')}
                                allowClear
                            >
                                {/* 只有当 hub 被选择时才显示仓库列表 */}
                                {form.getFieldValue('exceptionHubId') && (
                                    <>
                                        {/* 仓库列表 */}
                                        {warehouseListForHubData.map(warehouse => (
                                            <Select.Option
                                                key={warehouse.id}
                                                value={warehouse.id}
                                            >
                                                {warehouse.name}
                                            </Select.Option>
                                        ))}
                                    </>
                                )}
                            </Select>
                        </Form.Item>
                    </>
                )}

                {closeType !== 'LOST' && (
                    <Form.Item
                        name="imageS3Keys"
                        label={t('orderClose.fields.uploadPhotos')}
                    >
                        <ImageUploader
                            onChange={(urls) => {
                                form.setFieldsValue({ imageS3Keys: urls });
                            }}
                        />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default CloseOrderModal;
